import React, { useState, useMemo } from 'react';
import { InputActionMeta, MultiValueRemoveProps, Props } from 'react-select';
import {
  ITreeSelectExtendedOption,
  ITreeSelectOption,
  TTreeSelectOptionClick,
  TGroupedChildrenByParents,
} from './interfaces';

const unselectAllChildren = <SOption extends ITreeSelectOption = ITreeSelectOption>(
  selectProps: Props<SOption>,
  childValues: SOption['value'][],
  data: SOption,
) => {
  const selectedOptions = selectProps.value as SOption[];
  const values = selectedOptions.filter(({ value }) => (value !== data.value) && !childValues.includes(value));

  selectProps.onChange(values, {
    action: 'clear',
    removedValues: values,
    name: selectProps.name,
  });
};

const selectAllChildren = <SOption extends ITreeSelectOption = ITreeSelectOption>(
  selectProps: Props<SOption>,
  children: SOption[],
  selectedOptions: SOption[],
  data: SOption,
) => {
  const selectedValues = selectedOptions.map(({ value }) => value);
  const values = [
    ...selectedOptions,
    ...children.filter(({ value, isDisabled }) => (
      !isDisabled && !selectedValues.includes(value)),
    ),
  ];

  selectProps.onChange(values, {
    action: 'select-option',
    option: data,
    name: selectProps.name,
  });
};

export const useTreeSelect = <
  SOption extends ITreeSelectOption = ITreeSelectOption,
>(baseOptions: SOption[]) => {
  const [selectedTreeParents, setSelectedTreeParents] = useState<string[]>([]);

  const {
    options,
    groupedChildrenByParents,
  }: {
    options: ITreeSelectExtendedOption[],
    groupedChildrenByParents: TGroupedChildrenByParents,
  } = useMemo(() => {
    const options: ITreeSelectExtendedOption[] = [];
    const groupedChildrenByParents: TGroupedChildrenByParents = new Map();

    const traverse = (baseOptions: SOption[], parent?: SOption) => {
      baseOptions.forEach((item) => {
        const { options: childOptions, countryCode, ...otherValues } = item;

        const option = {
          ...otherValues,
          ...(!parent && { isParent: true }),
          ...(parent && {
            parent: parent.label,
            isDisabled: parent.isDisabled || item.isDisabled,
          }),
          ...(countryCode && parent && { countryCode: item.countryCode }),
          isValid: item.isValid ?? true,
        } satisfies ITreeSelectExtendedOption;

        options.push(option);

        if (option.isParent) {
          groupedChildrenByParents.set(item.label, { options: [], optionsValues: [] });

          if (childOptions) {
            traverse(childOptions as SOption[], item);
          }
        } else if (parent) {
          const { options, optionsValues } = groupedChildrenByParents.get(parent.label);

          options.push(option);
          optionsValues.push(option.value);
        }
      });
    };

    traverse(baseOptions as SOption[]);

    return { options, groupedChildrenByParents };
  }, [baseOptions]);

  const toggleParentOption = (event: React.MouseEvent, value: string) => {
    event.stopPropagation();

    const values = selectedTreeParents.includes(value)
      ? selectedTreeParents.filter((selectedParent) => selectedParent !== value)
      : [...selectedTreeParents, value];

    setSelectedTreeParents(values);
  };

  const optionClickHandler = (event: React.MouseEvent, { data, selectProps }: TTreeSelectOptionClick) => {
    if (!data.isParent || data.isDisabled) {
      return;
    }

    const { options } = groupedChildrenByParents.get(data.label);

    if (options.length > 0) {
      event.stopPropagation();

      const selectedOptions = selectProps.value as SOption[];
      const selectedValues = selectedOptions.map(({ value }) => value);
      const childValues = options
        .filter(({ isDisabled }) => !isDisabled)
        .map(({ value }) => value);

      childValues.every((value) => selectedValues.includes(value))
        ? unselectAllChildren(selectProps, childValues, data)
        : selectAllChildren(selectProps, options, selectedOptions, data);

      // clear input after click (searching case)
      selectProps.onInputChange('', { action: 'set-value', prevInputValue: data.value.toString() });
    }
  };

  const multiValueRemoveClickHandler = (props: MultiValueRemoveProps<ITreeSelectExtendedOption>) => {
    const { data, selectProps } = props;
    const selectedValues = selectProps.value as SOption[];
    const childrenValues = groupedChildrenByParents.get(data.parent)?.optionsValues;
    const selected = selectedValues.map(({ value }) => value);
    const isAllChildrenSelected = childrenValues?.every((child) => selected.includes(child));

    if (isAllChildrenSelected) {
      const values = selectedValues.filter(({ value }) => !childrenValues.includes(value));

      selectProps.onChange(values, {
        action: 'remove-value',
        removedValue: data,
        name: selectProps.name,
      });
    }
  };

  const handleInputChange = (search: string, meta: InputActionMeta) => {
    const { action, prevInputValue } = meta;

    if ((action === 'set-value' && prevInputValue) || action === 'menu-close') {
      setSelectedTreeParents([]);
      return;
    }

    if (action === 'input-change') {
      if (!search) {
        setSelectedTreeParents([]);
        return;
      }

      const lowerSearch = search.toLowerCase();
      const values: Set<string> = new Set();

      options.forEach(({ label, parent }) => {
        if (label.toLowerCase().includes(lowerSearch)) {
          values.add(parent);
        }
      });

      setSelectedTreeParents([...values]);
    }
  };

  const handleFilterOption = ({ label }: SOption, value: string) => {
    const lowerInputValue = value.toLowerCase();
    const childOptions = groupedChildrenByParents.get(label)?.options || [];

    return label.toLowerCase().includes(lowerInputValue) || childOptions.some((childOption) => (
      childOption.label.toLowerCase().includes(lowerInputValue)),
    );
  };

  return {
    options,
    groupedChildrenByParents,
    selectedTreeParents,
    toggleParentOption,
    optionClickHandler,
    multiValueRemoveClickHandler,
    handleInputChange,
    handleFilterOption,
  };
};
